/**
 * Field Activity Service
 *
 * Persists field activity events (on-site, completed, SOS, flagged) to Firestore.
 * Events are stored per day and linked to jobs/deals for permanent record-keeping.
 *
 * Collection: field_activity_log
 * Document ID: {date}_{eventId} (deterministic for idempotent writes)
 */

import { db } from '@/firebase';
import { collection, doc, setDoc, onSnapshot, query, where } from 'firebase/firestore';
import { getTenantIdSync, tenantIdReadUnion } from '@/lib/tenant';

export interface FieldActivityLog {
  id: string;
  eventId: string;
  type: 'on_site' | 'completed' | 'sos' | 'flagged' | 'request_more_time' | 'scheduled';
  title: string;
  subtitle: string;
  timestamp: string;
  severity: 'red' | 'amber' | 'orange' | 'green' | 'blue';
  date: string;
  jobId?: string;
  dealId?: string;
  techId?: string;
  techName?: string;
  customerName?: string;
  address?: string;
  jobType?: string;
  tenantId: string;
}

const COLLECTION = 'field_activity_log';

/**
 * Persist a field activity event. Uses setDoc with merge for idempotent writes
 * (safe to call from multiple clients simultaneously).
 *
 * Strips `undefined` values before writing — Firestore rejects undefined and
 * the optional fields on FieldActivityLog (dealId, techId, etc.) are commonly
 * absent for jobs without a linked deal.
 */
export async function persistFieldActivity(entry: FieldActivityLog): Promise<void> {
  const sanitized = Object.fromEntries(
    Object.entries(entry).filter(([, v]) => v !== undefined),
  ) as FieldActivityLog;
  await setDoc(doc(db, COLLECTION, entry.id), sanitized, { merge: true });
}

/**
 * Subscribe to field activity for a specific date.
 * Sorts client-side (no orderBy); the (tenantId, date) composite index lives in
 * firestore.indexes.json.
 */
export function subscribeFieldActivityByDate(
  date: string,
  callback: (items: FieldActivityLog[]) => void
): () => void {
  // tenantId filter REQUIRED by the rules lock — field_activity_log is
  // tenant-isolated (rules are not filters).
  const q = query(
    collection(db, COLLECTION),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('date', '==', date)
  );

  return onSnapshot(q, (snapshot) => {
    const items = snapshot.docs.map((d) => ({
      ...d.data(),
      id: d.id,
    } as FieldActivityLog));
    items.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
    callback(items);
  });
}
